// StageLook: lightweight scroll-snap slider with arrows and dots.
if (!customElements.get('sl-slider')) {
  customElements.define('sl-slider', class extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      this.prev = this.querySelector('[data-prev]');
      this.next = this.querySelector('[data-next]');
      this.dots = [...this.querySelectorAll('[data-dot]')];
      if (!this.track) return;
      const step = () => this.track.clientWidth * (this.dataset.step === 'item' ? 0.25 : 1);
      this.prev?.addEventListener('click', () => this.track.scrollBy({ left: -step(), behavior: 'smooth' }));
      this.next?.addEventListener('click', () => this.track.scrollBy({ left: step(), behavior: 'smooth' }));
      this.dots.forEach((d, i) => d.addEventListener('click', () =>
        this.track.scrollTo({ left: i * this.track.clientWidth, behavior: 'smooth' })));
      this.track.addEventListener('scroll', () => this.update(), { passive: true });
      this.update();
    }
    update() {
      const t = this.track;
      const max = t.scrollWidth - t.clientWidth - 2;
      if (this.prev) this.prev.disabled = t.scrollLeft <= 2;
      if (this.next) this.next.disabled = t.scrollLeft >= max;
      if (this.dots.length) {
        const i = Math.round(t.scrollLeft / t.clientWidth);
        this.dots.forEach((d, n) => d.setAttribute('aria-current', n === i ? 'true' : 'false'));
      }
    }
  });
}

// StageLook: infinite scroll for collection grids (falls back to the "Load more" link).
if (!customElements.get('sl-infinite')) {
  customElements.define('sl-infinite', class extends HTMLElement {
    connectedCallback() {
      this.loading = false;
      this.btn = this.querySelector('.sl-inf__btn');
      this.btn?.addEventListener('click', (e) => { e.preventDefault(); this.load(); });
      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver((entries) => {
          if (entries.some((en) => en.isIntersecting)) this.load();
        }, { rootMargin: '800px 0px' });
        this.io.observe(this);
      }
    }
    disconnectedCallback() { this.io?.disconnect(); }
    async load() {
      const next = this.dataset.next;
      if (this.loading || !next) return;
      this.loading = true;
      this.classList.add('is-loading');
      try {
        const url = next + (next.includes('?') ? '&' : '?') + 'section_id=' + this.dataset.section;
        const html = await fetch(url).then((r) => r.text());
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const grid = document.getElementById('product-grid');
        doc.querySelectorAll('#product-grid > li').forEach((li) => grid.appendChild(document.adoptNode(li)));
        const nextEl = doc.querySelector('sl-infinite');
        const status = doc.querySelector('.sl-inf__status');
        if (nextEl) {
          this.dataset.next = nextEl.dataset.next;
          if (this.btn) this.btn.href = nextEl.dataset.next;
          if (status) this.querySelector('.sl-inf__status').textContent = status.textContent;
        } else {
          const end = document.createElement('p');
          end.className = 'sl-inf__status sl-inf__status--end';
          end.textContent = status ? status.textContent : '';
          this.replaceWith(end);
        }
      } catch (e) {
        // keep the Load more link working as a normal page link
        if (this.btn) window.location.href = this.btn.href;
      } finally {
        this.loading = false;
        this.classList.remove('is-loading');
      }
    }
  });
}

// StageLook: quick add straight into Dawn's cart drawer (falls back to a normal form post).
document.addEventListener('submit', async (e) => {
  const form = e.target.closest && e.target.closest('form.sl-qa');
  const drawer = document.querySelector('cart-drawer');
  if (!form || !drawer || !window.routes) return;
  e.preventDefault();
  const btn = form.querySelector('button');
  btn.disabled = true;
  btn.classList.add('is-loading');
  const fd = new FormData(form);
  fd.append('sections', drawer.getSectionsToRender().map((s) => s.id));
  fd.append('sections_url', window.location.pathname);
  try {
    const res = await fetch(window.routes.cart_add_url, {
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' },
      body: fd,
    }).then((r) => r.json());
    if (res.status) {
      btn.setAttribute('title', res.description || 'Could not add this item');
      return;
    }
    drawer.setActiveElement(btn);
    drawer.renderContents(res);
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'sl-quick-add', productVariantId: fd.get('id'), cartData: res });
    }
  } catch (err) {
    form.submit();
  } finally {
    btn.disabled = false;
    btn.classList.remove('is-loading');
  }
});

// StageLook: "More from this brand" row, loaded after the page renders.
if (!customElements.get('sl-brand-row')) {
  customElements.define('sl-brand-row', class extends HTMLElement {
    connectedCallback() {
      const load = () => this.load();
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver((en) => { if (en.some((x) => x.isIntersecting)) { io.disconnect(); load(); } }, { rootMargin: '600px 0px' });
        io.observe(this);
      } else { load(); }
    }
    async load() {
      const section = this.closest('.shopify-section');
      try {
        const html = await fetch(this.dataset.url).then((r) => r.text());
        const doc = new DOMParser().parseFromString('<ul>' + html + '</ul>', 'text/html');
        const items = [...doc.querySelectorAll('li.sl-car__item')].filter((li) => li.dataset.handle !== this.dataset.exclude);
        if (!items.length) { if (section) section.hidden = true; return; }
        const track = this.querySelector('[data-track]');
        items.slice(0, 12).forEach((li) => track.appendChild(document.adoptNode(li)));
        this.querySelector('sl-slider')?.update?.();
      } catch (e) {
        if (section) section.hidden = true;
      }
    }
  });
}

// StageLook: sticky add-to-bag bar, shown once the main button scrolls out of view.
if (!customElements.get('sl-sticky-atc')) {
  customElements.define('sl-sticky-atc', class extends HTMLElement {
    connectedCallback() {
      this.main = document.querySelector('product-form [type="submit"][name="add"]');
      if (!this.main || !('IntersectionObserver' in window)) return;
      this.hidden = false;
      this.querySelector('.sl-satc__btn').addEventListener('click', () => {
        if (this.main.getAttribute('aria-disabled') === 'true' || this.main.disabled) {
          this.main.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else {
          this.main.click();
        }
      });
      new IntersectionObserver(([en]) => {
        const below = en.boundingClientRect.top < 0;
        this.classList.toggle('is-visible', !en.isIntersecting && below);
      }).observe(this.main);
      document.addEventListener('change', () => {
        const priceEl = document.querySelector('.product__info-container .price-item--regular, .product__info-container .price-item--sale');
        if (priceEl) this.querySelector('[data-price]').textContent = priceEl.textContent.trim();
      });
    }
  });
}
